/**
 * GenerateScheduleModal
 *
 * Unified schedule builder replacing the old "Auto Schedule" + "Custom Schedule" buttons.
 *
 * Left panel  – Date slot selection (weekly, collapsible)
 * Right panel – Pairing system per selected date:
 *     • Auto-generate round-robin pairs for a date
 *     • Manual pair: A vs B × (1-5) games (teams, players or pairs)
 *     • Expand / collapse per date
 * Footer      – Summary stats + Save
 * Post-save   – Inline preview with Confirm / Confirm All
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { apiClient } from '../../services/api/apiService';
import { generateAvailableSlots } from './scheduleAlgorithm';
import { scheduleErrorMessage, sendWithRosterGoAhead } from './leagueApi';
import { getLeagueFormat } from './leagueFormat';
import './GenerateScheduleModal.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

const COUNT_OPTIONS = [1, 2, 3, 4, 5];

/** Unique pair key regardless of order */
const pairKey = (a, b) => [a, b].sort((x, y) => x - y).join('-');

/**
 * Berger circle-method round-robin round generator.
 * Returns an array of rounds, each round being an array of [teamA, teamB] pairs.
 * Each team appears at most once per round (N-1 rounds for N teams).
 * With odd N, one team gets a bye each round.
 */
function bergerRounds(teamIds) {
  const n = teamIds.length;
  if (n < 2) return [];

  // Pad to even count with a sentinel "bye" (-1)
  const ids = n % 2 === 0 ? [...teamIds] : [...teamIds, -1];
  const half = ids.length / 2;
  const rounds = [];

  for (let r = 0; r < ids.length - 1; r++) {
    const roundPairs = [];
    for (let i = 0; i < half; i++) {
      const a = ids[i];
      const b = ids[ids.length - 1 - i];
      if (a !== -1 && b !== -1) {           // skip bye slots
        roundPairs.push([a, b]);
      }
    }
    rounds.push(roundPairs);

    // Rotate all except the first element (classic Berger rotation)
    const last = ids.pop();
    ids.splice(1, 0, last);
  }

  return rounds;
}

/**
 * Distribute round-robin pairings across selected dates using the Berger circle
 * method, so every team plays every other team exactly once across all dates.
 *
 * Each date fills as many rounds as its court count allows (one pair per court
 * per time slot). Once all matchups are assigned the remainder of the dates
 * are left empty (or filled with repeated rounds if the caller selects more
 * dates than needed).
 */
function distributeRoundRobin(teams, dates, courtsPerDate) {
  if (!teams.length || !dates.length) return {};
  const result = {};
  dates.forEach(d => { result[d.slotKey] = []; });

  const ids    = teams.map(t => t.team_id ?? t.id);
  const rounds = bergerRounds(ids);     // proper rotation: each team once per round

  // Flatten rounds into individual pair assignments
  const pairAssignments = [];
  rounds.forEach(round => {
    round.forEach(pair => pairAssignments.push(pair));
  });

  // Distribute pairs across dates, up to courtsPerDate per date
  let pairIdx = 0;
  for (const date of dates) {
    const key  = date.slotKey;
    let   slot = 0;
    while (slot < courtsPerDate && pairIdx < pairAssignments.length) {
      const [a, b] = pairAssignments[pairIdx];
      const t1 = teams.find(t => (t.team_id ?? t.id) === a);
      const t2 = teams.find(t => (t.team_id ?? t.id) === b);
      result[key] = [...(result[key] || []), {
        id:    `${pairIdx}-${Date.now()}`,
        team1: a,
        team2: b,
        name1: t1?.team_name ?? t1?.name ?? `#${a}`,
        name2: t2?.team_name ?? t2?.name ?? `#${b}`,
        count: 1,
      }];
      pairIdx++;
      slot++;
    }
    if (pairIdx >= pairAssignments.length) break;
  }
  return result;
}

// ── Main component ────────────────────────────────────────────────────────────

const GenerateScheduleModal = ({
  isOpen,
  league,
  teams,
  onClose,
  onScheduleCreated,
  showFeedback,
  openConfirm,
}) => {
  // ── State ──────────────────────────────────────────────────────────────────
  const [selectedSlotKeys, setSelectedSlotKeys] = useState(new Set());
  const [pairingsBySlot, setPairingsBySlot]     = useState({});   // slotKey → [{id, team1, team2, name1, name2, count}]
  const [collapsedSlots, setCollapsedSlots]     = useState(new Set());
  const [draftBySlot, setDraftBySlot]           = useState({});   // slotKey → {team1:'', team2:''}
  const [isSaving, setIsSaving]                 = useState(false);
  const [preview, setPreview]                   = useState(null);  // array of saved schedule entries
  const [confirmedIds, setConfirmedIds]         = useState(new Set());

  // ── Derived ────────────────────────────────────────────────────────────────
  const availableSlots = useMemo(() => generateAvailableSlots(league), [league]);
  const { unit, unitLower } = getLeagueFormat(league);

  const slotsByWeek = useMemo(() => {
    const map = new Map();
    availableSlots.forEach(s => {
      const wk = s.weekNumber;
      if (!map.has(wk)) map.set(wk, []);
      map.get(wk).push(s);
    });
    return Array.from(map.entries()).map(([wk, slots]) => ({ weekNumber: wk, slots }));
  }, [availableSlots]);

  const selectedSlots = useMemo(
    () => availableSlots.filter(s => selectedSlotKeys.has(s.slotKey)),
    [availableSlots, selectedSlotKeys]
  );

  const totalGames = useMemo(
    () => Object.values(pairingsBySlot).flat().reduce((s, p) => s + (p.count || 1), 0),
    [pairingsBySlot]
  );

  const totalPairs = useMemo(
    () => Object.values(pairingsBySlot).flat().length,
    [pairingsBySlot]
  );

  // ── Keyboard close ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // ── Slot selection ─────────────────────────────────────────────────────────
  const toggleSlot = useCallback((slotKey) => {
    setSelectedSlotKeys(prev => {
      const next = new Set(prev);
      if (next.has(slotKey)) {
        next.delete(slotKey);
        setPairingsBySlot(p => { const n = { ...p }; delete n[slotKey]; return n; });
      } else {
        next.add(slotKey);
      }
      return next;
    });
  }, []);

  const selectAllSlots = useCallback(() => {
    const allKeys = new Set(availableSlots.map(s => s.slotKey));
    setSelectedSlotKeys(allKeys);
  }, [availableSlots]);

  const clearAllSlots = useCallback(() => {
    setSelectedSlotKeys(new Set());
    setPairingsBySlot({});
  }, []);

  const toggleWeek = useCallback((weekSlots) => {
    const allSelected = weekSlots.every(s => selectedSlotKeys.has(s.slotKey));
    setSelectedSlotKeys(prev => {
      const next = new Set(prev);
      weekSlots.forEach(s => allSelected ? next.delete(s.slotKey) : next.add(s.slotKey));
      return next;
    });
    if (allSelected) {
      setPairingsBySlot(p => {
        const n = { ...p };
        weekSlots.forEach(s => delete n[s.slotKey]);
        return n;
      });
    }
  }, [selectedSlotKeys]);

  // ── Auto-generate (all selected slots) ────────────────────────────────────
  const handleAutoGenerateAll = useCallback(() => {
    if (!selectedSlots.length) {
      showFeedback?.('warning', 'Select at least one date slot first.');
      return;
    }
    const courts = league?.number_of_courts || 1;
    const dist = distributeRoundRobin(teams, selectedSlots, courts);
    setPairingsBySlot(prev => ({ ...prev, ...dist }));
    showFeedback?.('success', `Auto-generated pairings across ${selectedSlots.length} date(s).`);
  }, [selectedSlots, teams, league, showFeedback]);

  // ── Auto-generate per date ─────────────────────────────────────────────────
  const handleAutoGenerateDate = useCallback((slotKey) => {
    const courts = league?.number_of_courts || 1;
    const dist = distributeRoundRobin(teams, [availableSlots.find(s => s.slotKey === slotKey)], courts);
    setPairingsBySlot(prev => ({ ...prev, [slotKey]: dist[slotKey] || [] }));
  }, [teams, league, availableSlots]);

  // ── Pair CRUD ──────────────────────────────────────────────────────────────
  const addPair = useCallback((slotKey) => {
    const draft = draftBySlot[slotKey] || {};
    const t1 = parseInt(draft.team1 || '0');
    const t2 = parseInt(draft.team2 || '0');
    if (!t1 || !t2)        { showFeedback?.('warning', 'Select both teams.');   return; }
    if (t1 === t2)         { showFeedback?.('warning', `A ${unitLower} cannot play itself.`); return; }

    const existing = pairingsBySlot[slotKey] || [];
    const key = pairKey(t1, t2);
    if (existing.some(p => pairKey(p.team1, p.team2) === key)) {
      showFeedback?.('warning', 'This matchup is already added for this date.');
      return;
    }
    const t1obj = teams.find(t => (t.team_id ?? t.id) === t1);
    const t2obj = teams.find(t => (t.team_id ?? t.id) === t2);
    setPairingsBySlot(prev => ({
      ...prev,
      [slotKey]: [...(prev[slotKey] || []), {
        id: `${Date.now()}-${Math.random()}`,
        team1: t1,
        team2: t2,
        name1: t1obj?.team_name ?? t1obj?.name ?? `#${t1}`,
        name2: t2obj?.team_name ?? t2obj?.name ?? `#${t2}`,
        count: parseInt(draft.count || '1'),
      }],
    }));
    setDraftBySlot(prev => ({ ...prev, [slotKey]: {} }));
  }, [draftBySlot, pairingsBySlot, teams, showFeedback]);

  const removePair = useCallback((slotKey, pairId) => {
    setPairingsBySlot(prev => ({
      ...prev,
      [slotKey]: (prev[slotKey] || []).filter(p => p.id !== pairId),
    }));
  }, []);

  const updatePairCount = useCallback((slotKey, pairId, count) => {
    setPairingsBySlot(prev => ({
      ...prev,
      [slotKey]: (prev[slotKey] || []).map(p => p.id === pairId ? { ...p, count } : p),
    }));
  }, []);

  const setDraft = useCallback((slotKey, field, val) => {
    setDraftBySlot(prev => ({ ...prev, [slotKey]: { ...(prev[slotKey] || {}), [field]: val } }));
  }, []);

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!selectedSlots.length) { showFeedback?.('warning', 'Select at least one date slot.'); return; }
    if (totalPairs === 0) { showFeedback?.('warning', `Add at least one ${unitLower} pairing.`); return; }

    const slot = availableSlots[0]; // for time reference
    const startTimeParts = (league?.start_time || '18:00').split(':').map(Number);
    const baseMinutes = startTimeParts[0] * 60 + (startTimeParts[1] || 0);
    const gameGap = 75; // minutes between games on same court

    const scheduleEntries = [];
    let weekCounter = 1;
    let prevWeekNum = null;

    selectedSlots.forEach((slot) => {
      const pairs = pairingsBySlot[slot.slotKey] || [];
      if (!pairs.length) return;
      if (slot.weekNumber !== prevWeekNum) { weekCounter++; prevWeekNum = slot.weekNumber; }

      const dateStr = slot.date.toISOString().split('T')[0];
      let courtIdx = 0;
      let timeOffset = 0;

      pairs.forEach(pair => {
        const repetitions = pair.count || 1;
        for (let r = 0; r < repetitions; r++) {
          const court = (courtIdx % (league?.number_of_courts || 1)) + 1;
          const mins  = baseMinutes + timeOffset;
          const hh    = String(Math.floor(mins / 60) % 24).padStart(2, '0');
          const mm    = String(mins % 60).padStart(2, '0');
          scheduleEntries.push({
            league_id:    league?.id,
            game_date:    dateStr,
            game_time:    `${hh}:${mm}:00`,
            court_number: court,
            week_number:  slot.weekNumber,
            team_1_id:    pair.team1,
            team_2_id:    pair.team2,
            status:       'scheduled',
            match_type:   'regular_season',
            // extra metadata for preview
            _team1Name: pair.name1,
            _team2Name: pair.name2,
          });
          courtIdx++;
          if (courtIdx % (league?.number_of_courts || 1) === 0) timeOffset += gameGap;
        }
      });
    });

    if (!scheduleEntries.length) {
      showFeedback?.('warning', 'No valid pairings to save. Ensure selected dates have pairings.');
      return;
    }

    setIsSaving(true);
    try {
      const saved = await sendWithRosterGoAhead((extra) =>
        apiClient.post(`/leagues/${league.id}/custom-schedule`, { schedule: scheduleEntries, ...extra }));
      if (!saved) return;
      setPreview(scheduleEntries);
      setConfirmedIds(new Set());
      showFeedback?.('success', `${scheduleEntries.length} game(s) saved across ${selectedSlots.length} date(s).`);
    } catch (err) {
      showFeedback?.('error', scheduleErrorMessage(err) || 'Failed to save schedule.');
    } finally {
      setIsSaving(false);
    }
  }, [selectedSlots, pairingsBySlot, availableSlots, league, totalPairs, showFeedback]);

  // ── Confirm matches ────────────────────────────────────────────────────────
  const handleConfirmEntry = useCallback(async (entry, idx) => {
    try {
      const res = await sendWithRosterGoAhead((extra) => apiClient.post(`/leagues/${league.id}/bulk-matches`, {
        matches: [entry],
        schedule_type: 'scheduled',
        ...extra,
      }));
      if (!res) return;
      setConfirmedIds(prev => new Set([...prev, idx]));
      showFeedback?.('success', `Match created: ${entry._team1Name} vs ${entry._team2Name}`);
    } catch (err) {
      showFeedback?.('error', err.response?.data?.message || 'Failed to create match.');
    }
  }, [league, showFeedback]);

  const handleConfirmAll = useCallback(async () => {
    if (!preview?.length) return;
    try {
      const cleanEntries = preview.map(({ _team1Name, _team2Name, ...e }) => e);
      const res = await sendWithRosterGoAhead((extra) => apiClient.post(`/leagues/${league.id}/bulk-matches`, {
        matches: cleanEntries,
        schedule_type: 'scheduled',
        ...extra,
      }));
      if (!res) return;
      const created = res?.data?.data?.total_created ?? preview.length;
      const skipped = res?.data?.data?.skipped_existing || 0;
      setConfirmedIds(new Set(preview.map((_, i) => i)));
      showFeedback?.('success', skipped > 0
        ? `${created} match(es) created; ${skipped} already had a match.`
        : `All ${created} match(es) created successfully!`);
      onScheduleCreated?.(preview);
    } catch (err) {
      showFeedback?.('error', err.response?.data?.message || 'Failed to create matches.');
    }
  }, [preview, league, showFeedback, onScheduleCreated]);

  if (!isOpen) return null;

  // ── Render ─────────────────────────────────────────────────────────────────
  return createPortal(
    <div
      className="gen-schedule-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Generate Schedule"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="gen-schedule-modal" onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="gen-schedule-header">
          <h2>📅 Generate Schedule</h2>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {availableSlots.length > 0 && selectedSlots.length > 0 && !preview && (
              <button
                className="gen-auto-btn"
                onClick={handleAutoGenerateAll}
                title="Auto-generate round-robin pairings for all selected dates"
              >
                ⚙️ Auto Round-Robin
              </button>
            )}
            <button className="gen-cancel-btn" onClick={onClose}>✕ Cancel</button>
          </div>
        </div>

        {/* ── Body (two-column) ── */}
        {!preview && (
          <div className="gen-schedule-body">
            {/* Left: Date Slots */}
            <div className="gen-slots-panel">
              <h3>📆 Select Date Slots</h3>
              {availableSlots.length === 0 ? (
                <div className="gen-no-slots">
                  No date slots available.<br />
                  Ensure the league has season start/end dates and at least one game day configured.
                </div>
              ) : (
                <>
                  <div className="gen-slots-controls">
                    <button className="gen-slots-btn" onClick={selectAllSlots}>
                      Select All ({availableSlots.length})
                    </button>
                    <button className="gen-slots-btn" onClick={clearAllSlots}>Clear All</button>
                  </div>
                  {slotsByWeek.map(({ weekNumber, slots }) => {
                    const allSel = slots.every(s => selectedSlotKeys.has(s.slotKey));
                    const someSel = slots.some(s => selectedSlotKeys.has(s.slotKey));
                    return (
                      <div key={weekNumber} className="gen-week-group">
                        <button
                          className={`gen-week-label ${allSel ? 'selected' : someSel ? 'partial' : ''}`}
                          onClick={() => toggleWeek(slots)}
                        >
                          Week {weekNumber}
                          <span className="gen-week-badge">{slots.length} day{slots.length !== 1 ? 's' : ''}</span>
                        </button>
                        <div className="gen-date-cards">
                          {slots.map(slot => {
                            const sel  = selectedSlotKeys.has(slot.slotKey);
                            const pairs = pairingsBySlot[slot.slotKey]?.length || 0;
                            return (
                              <div
                                key={slot.slotKey}
                                className={`gen-date-card ${sel ? 'selected' : ''}`}
                                onClick={() => toggleSlot(slot.slotKey)}
                              >
                                <span className="date-check">{sel ? '✓' : ''}</span>
                                <div className="gen-date-info">
                                  <div className="date-day">{slot.dayLabel}</div>
                                  <div className="date-date">{slot.formatted}</div>
                                </div>
                                {sel && pairs > 0 && (
                                  <span className="gen-date-pairs-count">{pairs} pair{pairs !== 1 ? 's' : ''}</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            {/* Right: Pairing system */}
            <div className="gen-pairing-panel">
              <h3>🆚 {unit} Pairings</h3>
              {selectedSlots.length === 0 ? (
                <div className="gen-no-dates-selected">
                  <span style={{ fontSize: '1.8rem' }}>👆</span>
                  <span>Select date slots on the left to start pairing teams.</span>
                </div>
              ) : (
                selectedSlots.map(slot => {
                  const isCollapsed = collapsedSlots.has(slot.slotKey);
                  const pairs = pairingsBySlot[slot.slotKey] || [];
                  const draft = draftBySlot[slot.slotKey] || {};

                  return (
                    <div key={slot.slotKey} className="gen-date-pairing-block">
                      <div
                        className="gen-date-pairing-header"
                        onClick={() => setCollapsedSlots(prev => {
                          const n = new Set(prev);
                          n.has(slot.slotKey) ? n.delete(slot.slotKey) : n.add(slot.slotKey);
                          return n;
                        })}
                      >
                        <div className="gen-date-pairing-title">
                          {slot.dayLabel} {slot.formatted}
                          {pairs.length > 0 && (
                            <span className="badge">
                              {pairs.reduce((s, p) => s + (p.count || 1), 0)} game{pairs.reduce((s, p) => s + (p.count || 1), 0) !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        <div className="gen-date-pairing-actions" onClick={e => e.stopPropagation()}>
                          <button
                            className="gen-auto-btn"
                            onClick={() => handleAutoGenerateDate(slot.slotKey)}
                            title="Auto round-robin for this date"
                          >
                            ⚙️ Auto
                          </button>
                          <button className="gen-collapse-btn">
                            {isCollapsed ? '▼' : '▲'}
                          </button>
                        </div>
                      </div>

                      {!isCollapsed && (
                        <div className="gen-date-pairs-body">
                          {/* Existing pairs */}
                          {pairs.length === 0 && (
                            <div className="gen-no-pairs">No pairings yet — use Auto or add manually.</div>
                          )}
                          {pairs.map(pair => (
                            <div key={pair.id} className="gen-pair-row">
                              <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', minWidth: '1.2rem' }}>🏐</span>
                              <span style={{ fontSize: '0.78rem', color: '#e2e8f0', flex: 1 }}>{pair.name1}</span>
                              <span className="gen-vs-label">vs</span>
                              <span style={{ fontSize: '0.78rem', color: '#e2e8f0', flex: 1 }}>{pair.name2}</span>
                              <select
                                className="gen-count-select"
                                value={pair.count}
                                onChange={e => updatePairCount(slot.slotKey, pair.id, parseInt(e.target.value))}
                                onClick={e => e.stopPropagation()}
                              >
                                {COUNT_OPTIONS.map(n => (
                                  <option key={n} value={n}>{n}×</option>
                                ))}
                              </select>
                              <button
                                className="gen-remove-pair-btn"
                                onClick={() => removePair(slot.slotKey, pair.id)}
                                title="Remove this pairing"
                              >
                                ✕
                              </button>
                            </div>
                          ))}

                          {/* Add pair row */}
                          <div className="gen-add-pair-row" style={{ marginTop: '0.5rem' }}>
                            <select
                              className="gen-team-select"
                              value={draft.team1 || ''}
                              onChange={e => setDraft(slot.slotKey, 'team1', e.target.value)}
                            >
                              <option value="">{unit} A</option>
                              {teams.map(t => {
                                const id = t.team_id ?? t.id;
                                const disabled = String(id) === String(draft.team2 || '');
                                return (
                                  <option key={id} value={id} disabled={disabled}>
                                    {t.team_name || t.name}
                                  </option>
                                );
                              })}
                            </select>
                            <span className="gen-vs-label">vs</span>
                            <select
                              className="gen-team-select"
                              value={draft.team2 || ''}
                              onChange={e => setDraft(slot.slotKey, 'team2', e.target.value)}
                            >
                              <option value="">{unit} B</option>
                              {teams.map(t => {
                                const id = t.team_id ?? t.id;
                                const disabled = String(id) === String(draft.team1 || '');
                                return (
                                  <option key={id} value={id} disabled={disabled}>
                                    {t.team_name || t.name}
                                  </option>
                                );
                              })}
                            </select>
                            <select
                              className="gen-count-select"
                              value={draft.count || 1}
                              onChange={e => setDraft(slot.slotKey, 'count', e.target.value)}
                            >
                              {COUNT_OPTIONS.map(n => (
                                <option key={n} value={n}>{n}×</option>
                              ))}
                            </select>
                            <button
                              className="gen-add-pair-btn"
                              onClick={() => addPair(slot.slotKey)}
                            >
                              + Add
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* ── Post-save preview ── */}
        {preview && (
          <div className="gen-preview-section">
            <div className="gen-preview-header">
              <h4>📋 Review &amp; Confirm Matches ({preview.length} total)</h4>
              <button className="gen-confirm-all-btn" onClick={handleConfirmAll}>
                ✅ Confirm All
              </button>
            </div>
            {(() => {
              const byWeek = new Map();
              preview.forEach((e, i) => {
                if (!byWeek.has(e.week_number)) byWeek.set(e.week_number, []);
                byWeek.get(e.week_number).push({ ...e, _idx: i });
              });
              return Array.from(byWeek.entries()).map(([wk, entries]) => (
                <div key={wk} className="gen-preview-week">
                  <div className="gen-preview-week-label">Week {wk}</div>
                  {entries.map(e => {
                    const done = confirmedIds.has(e._idx);
                    return (
                      <div key={e._idx} className={`gen-preview-game ${done ? 'confirmed' : ''}`}>
                        <div>
                          <div className="matchup">{e._team1Name} vs {e._team2Name}</div>
                          <div className="meta">{e.game_date} • {e.game_time?.slice(0,5)} • Court {e.court_number}</div>
                        </div>
                        {done
                          ? <span className="confirmed-badge">✅ Confirmed</span>
                          : (
                            <button className="confirm-btn" onClick={() => handleConfirmEntry(e, e._idx)}>
                              Confirm
                            </button>
                          )
                        }
                      </div>
                    );
                  })}
                </div>
              ));
            })()}
          </div>
        )}

        {/* ── Footer ── */}
        <div className="gen-schedule-footer">
          <div className="gen-summary">
            <div className="gen-summary-item">
              <span className="label">Dates</span>
              <span className="value">{selectedSlots.length}</span>
            </div>
            <div className="gen-summary-item">
              <span className="label">Pairs</span>
              <span className="value">{totalPairs}</span>
            </div>
            <div className="gen-summary-item">
              <span className="label">Total Games</span>
              <span className="value">{totalGames}</span>
            </div>
            {preview && (
              <div className="gen-summary-item">
                <span className="label">Confirmed</span>
                <span className="value">{confirmedIds.size} / {preview.length}</span>
              </div>
            )}
          </div>
          <div className="gen-footer-actions">
            {preview ? (
              <button className="gen-cancel-btn" onClick={onClose}>Close</button>
            ) : (
              <button
                className="gen-save-btn"
                onClick={handleSave}
                disabled={isSaving || selectedSlots.length === 0 || totalPairs === 0}
              >
                {isSaving ? 'Saving…' : `💾 Save ${totalGames} Game${totalGames !== 1 ? 's' : ''}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GenerateScheduleModal;
